import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Checkbox } from '@/shared/components/ui/checkbox';
import type { ProviderEditorFormData } from '../schemas';

type ServicesOfferingsSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * ServicesOfferingsSection - Service delivery methods and pricing
 * 
 * Contains:
 * - Service ranges (IN_OFFICE, VIRTUAL, IN_HOME)
 * - Price range indicator
 * - Price details text
 * - Emergency response availability
 * - Emergency response details
 */
export function ServicesOfferingsSection({
  control,
  errors,
}: ServicesOfferingsSectionProps) {
  return (
    <div className="space-y-6">
      {/* Service Ranges (delivery methods) */}
      <FormField
        label="Service Delivery Methods"
        error={errors.provider?.serviceRanges?.message}
        hint="Select all that apply"
      >
        <Controller
          name="provider.serviceRanges"
          control={control}
          render={({ field }) => {
            const value = field.value || [];
            const toggleOption = (option: 'IN_OFFICE' | 'VIRTUAL' | 'IN_HOME') => {
              if (value.includes(option)) {
                field.onChange(value.filter((v) => v !== option));
              } else {
                field.onChange([...value, option]);
              }
            };

            return (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="in-office"
                    checked={value.includes('IN_OFFICE')}
                    onCheckedChange={() => toggleOption('IN_OFFICE')}
                  />
                  <label
                    htmlFor="in-office"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    In-Office (clients visit your location)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="virtual"
                    checked={value.includes('VIRTUAL')}
                    onCheckedChange={() => toggleOption('VIRTUAL')}
                  />
                  <label
                    htmlFor="virtual"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Virtual (telehealth, video calls)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="in-home"
                    checked={value.includes('IN_HOME')}
                    onCheckedChange={() => toggleOption('IN_HOME')}
                  />
                  <label
                    htmlFor="in-home"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    In-Home (you visit the client's location)
                  </label>
                </div>
              </div>
            );
          }}
        />
      </FormField>

      {/* Pricing */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Price Range Indicator"
          htmlFor="price-range-indicator"
          error={errors.provider?.priceRangeIndicator?.message}
          hint="General price level"
        >
          <Controller
            name="provider.priceRangeIndicator"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                id="price-range-indicator"
                value={field.value || ''}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select price range</option>
                <option value="$">$ - Budget-friendly</option>
                <option value="$$">$$ - Moderate</option>
                <option value="$$$">$$$ - Premium</option>
                <option value="$$$$">$$$$ - Luxury</option>
              </select>
            )}
          />
        </FormField>

        <FormField
          label="Price Details"
          htmlFor="price-details"
          error={errors.provider?.priceDetailsText?.message}
          hint="Additional pricing information"
        >
          <Controller
            name="provider.priceDetailsText"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value || ''}
                id="price-details"
                maxLength={1570}
                className={errors.provider?.priceDetailsText ? 'border-red-500' : ''}
              />
            )}
          />
        </FormField>
      </div>

      {/* Emergency Response */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
        <h4 className="font-medium text-gray-900">Emergency Response</h4>

        <FormField
          label="Emergency Response Available"
          htmlFor="emergency-response-available"
          error={errors.provider?.emergencyResponseAvailable?.message}
        >
          <Controller
            name="provider.emergencyResponseAvailable"
            control={control}
            render={({ field }) => (
              <select
                id="emergency-response-available"
                value={field.value === true ? 'true' : 'false'}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value === 'true')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="true">Yes, Available</option>
                <option value="false">No, Not Available</option>
              </select>
            )}
          />
        </FormField>

        <FormField
          label="Emergency Response Details"
          htmlFor="emergency-response-details"
          error={errors.provider?.emergencyResponseDetails?.message}
          hint="Describe emergency services, hours, and response process"
        >
          <Controller
            name="provider.emergencyResponseDetails"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value || ''}
                id="emergency-response-details"
                rows={3}
                className={errors.provider?.emergencyResponseDetails ? 'border-red-500' : ''}
              />
            )}
          />
        </FormField>
      </div>
    </div>
  );
}

